import React, { Fragment, useCallback, useEffect, useState } from "react";
import Modal from "../../../components/Atomos/Modals/Modal";
import NewButton from "../../../components/Atomos/Buttons/newButton";
import Cancel from "../../../components/Atomos/Icons/Close";
import Excel from "../../../components/Atomos/Icons/Excel";
import {
  getPaymentReminderBatchLogs,
  type IPaymentReminderBatchLog,
  type IPaymentReminderLogRecipient,
} from "../../../Services/PaymentReminderLog";
import { formatDateToString } from "../../../utils/functions";
import {
  exportStyledExcel,
  getExcelTitleFromFilename,
} from "../../../utils/excel/exportStyledExcel";

interface PaymentReminderBatchLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId?: string;
  refreshKey?: number;
}

const REMINDER_LOG_LIMIT = 10;

const EXCEL_COLUMNS = [
  { key: "Batch #", header: "Batch #" },
  { key: "Batch sent at", header: "Batch sent at" },
  { key: "Sent by", header: "Sent by" },
  { key: "Selected", header: "Selected" },
  { key: "Sent", header: "Sent" },
  { key: "Failed", header: "Failed" },
  { key: "Skipped", header: "Skipped" },
  { key: "Recipient", header: "Recipient" },
  { key: "Outcome", header: "Outcome" },
  { key: "Reason", header: "Reason" },
] as const;

function getBatchSentAtLabel(log: IPaymentReminderBatchLog): string {
  return log.triggeredAt
    ? formatDateToString(new Date(log.triggeredAt))
    : "—";
}

function outcomeLabel(outcome: IPaymentReminderLogRecipient["outcome"]): string {
  if (outcome === "sent") {
    return "Sent";
  }
  if (outcome === "failed") {
    return "Failed";
  }
  return "Skipped";
}

function outcomeClass(outcome: IPaymentReminderLogRecipient["outcome"]): string {
  if (outcome === "sent") {
    return "text-emerald-700";
  }
  if (outcome === "failed") {
    return "text-red-700";
  }
  return "text-amber-700";
}

function buildExcelRows(
  logs: IPaymentReminderBatchLog[],
): Record<string, string | number>[] {
  const rows: Record<string, string | number>[] = [];

  logs.forEach((log, index) => {
    const batchNumber = index + 1;
    const batchSentAt = getBatchSentAtLabel(log);
    const baseRow = {
      "Batch #": batchNumber,
      "Batch sent at": batchSentAt,
      "Sent by": log.triggeredByName || "—",
      Selected: log.totalSelected,
      Sent: log.sentCount,
      Failed: log.failedCount,
      Skipped: log.skippedCount,
    };
    const recipients = log.recipients ?? [];

    if (recipients.length === 0) {
      rows.push({
        ...baseRow,
        Recipient: "—",
        Outcome: "—",
        Reason: "—",
      });
      return;
    }

    for (const recipient of recipients) {
      rows.push({
        ...baseRow,
        Recipient: recipient.personName,
        Outcome: outcomeLabel(recipient.outcome),
        Reason: recipient.reason || "",
      });
    }
  });

  return rows;
}

export default function PaymentReminderBatchLogModal({
  isOpen,
  onClose,
  eventId,
  refreshKey = 0,
}: PaymentReminderBatchLogModalProps): React.ReactElement {
  const [logs, setLogs] = useState<IPaymentReminderBatchLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getPaymentReminderBatchLogs({
        eventId,
        limit: REMINDER_LOG_LIMIT,
      });
      setLogs(rows);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (!isOpen) {
      setExpandedId(null);
      return;
    }
    void loadLogs();
  }, [isOpen, loadLogs, refreshKey]);

  const handleClose = () => {
    onClose();
  };

  const handleExportToExcel = async () => {
    if (logs.length === 0 || isExporting) {
      return;
    }

    setIsExporting(true);
    try {
      const filename = `reminder_send_history_${new Date().toISOString().slice(0, 10)}.xlsx`;
      await exportStyledExcel({
        filename,
        title: getExcelTitleFromFilename(filename),
        rows: buildExcelRows(logs),
        columns: EXCEL_COLUMNS.map((column) => ({
          key: column.key,
          header: column.header,
        })),
      });
    } catch (error) {
      console.error("Failed to export reminder logs:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal
      isModalOpen={isOpen}
      toggleModal={handleClose}
      title="Reminder send history"
      compact
      compactBodyScrolls={false}
      className="max-w-5xl w-full"
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-500">
          Showing the last {REMINDER_LOG_LIMIT} reminder batches for this event.
        </p>

        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : logs.length === 0 ? (
          <p className="text-sm text-gray-font">
            No reminder batches have been recorded yet.
          </p>
        ) : (
          <div className="max-h-[min(60vh,32rem)] overflow-y-auto overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b border-gray-200 text-gray-font">
                  <th className="px-2 py-2 font-semibold">Batch #</th>
                  <th className="px-2 py-2 font-semibold">Sent at</th>
                  <th className="px-2 py-2 font-semibold">Sent by</th>
                  <th className="px-2 py-2 font-semibold">Selected</th>
                  <th className="px-2 py-2 font-semibold">Sent</th>
                  <th className="px-2 py-2 font-semibold">Failed</th>
                  <th className="px-2 py-2 font-semibold">Skipped</th>
                  <th className="px-2 py-2 font-semibold">Recipients</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, index) => {
                  const logId = log.id || `${log.triggeredAt}-${log.triggeredById}`;
                  const isExpanded = expandedId === logId;
                  const recipients = log.recipients ?? [];
                  const batchNumber = index + 1;

                  return (
                    <Fragment key={logId}>
                      <tr className="border-b border-gray-100">
                        <td className="px-2 py-2 tabular-nums font-semibold text-Default">
                          {batchNumber}
                        </td>
                        <td className="px-2 py-2 tabular-nums whitespace-nowrap">
                          {getBatchSentAtLabel(log)}
                        </td>
                        <td className="px-2 py-2">{log.triggeredByName || "—"}</td>
                        <td className="px-2 py-2 tabular-nums">
                          {log.totalSelected}
                        </td>
                        <td className="px-2 py-2 tabular-nums text-emerald-700">
                          {log.sentCount}
                        </td>
                        <td className="px-2 py-2 tabular-nums text-red-700">
                          {log.failedCount}
                        </td>
                        <td className="px-2 py-2 tabular-nums text-amber-700">
                          {log.skippedCount}
                        </td>
                        <td className="px-2 py-2">
                          {recipients.length > 0 ? (
                            <button
                              type="button"
                              className="text-Default hover:underline"
                              onClick={() =>
                                setExpandedId(isExpanded ? null : logId)
                              }
                            >
                              {isExpanded
                                ? "Hide"
                                : `View ${recipients.length}`}
                            </button>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                      {isExpanded && recipients.length > 0 ? (
                        <tr>
                          <td colSpan={8} className="bg-gray-50 px-4 py-3">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Batch #{batchNumber} recipients
                            </p>
                            <ul className="max-h-48 overflow-y-auto space-y-2 text-sm text-gray-font">
                              {recipients.map((recipient) => (
                                <li key={recipient.registrationId}>
                                  <span className="font-medium">
                                    {recipient.personName}
                                  </span>
                                  <span
                                    className={`ml-2 font-medium ${outcomeClass(recipient.outcome)}`}
                                  >
                                    {outcomeLabel(recipient.outcome)}
                                  </span>
                                  {recipient.reason ? (
                                    <span className="text-gray-500">
                                      {" "}
                                      — {recipient.reason}
                                    </span>
                                  ) : null}
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex shrink-0 justify-end gap-3 border-t border-neutral-200 pt-4">
          <NewButton
            text={isExporting ? "Exporting…" : "Export to Excel"}
            icon={<Excel />}
            style="filled"
            size="medium"
            content="textIcon"
            onClick={() => void handleExportToExcel()}
            color="excel"
            disabled={loading || logs.length === 0 || isExporting}
            className="!w-auto shrink-0"
          />
          <NewButton
            text="Close"
            icon={<Cancel />}
            style="outlined"
            size="medium"
            content="textIcon"
            onClick={handleClose}
            color="neutral"
            className="!w-auto shrink-0 min-w-[7rem]"
          />
        </div>
      </div>
    </Modal>
  );
}
